/**
 * NotificationBell — header bell that catches users up on any push notifications
 * they missed while their device was off. Polls /api/push/inbox lightly when
 * mounted, listens for service-worker push events to refresh live, and lets
 * the user click any row to jump straight to the relevant URL.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Bell, Check, Inbox, X, BellOff, MoreHorizontal } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60_000; // light poll — service-worker pushes also trigger refresh

const ICONS = {
  weekly_winner: '🏆',
  trade_offer: '🤝',
  direct_message: '💬',
  pet_evolution: '✨',
  wheel_reminder: '🎡',
  flame_ready: '🔥',
  achievement: '🏅',
  raid_invite: '⚔️',
  friend_request: '🫂',
};

const TAG_LABEL = {
  weekly_winner: 'weekly rewards',
  trade_offer: 'trade offers',
  direct_message: 'direct messages',
  pet_evolution: 'pet evolution',
  wheel_reminder: 'daily wheel',
  flame_ready: 'eternal flame',
  achievement: 'achievements',
  raid_invite: 'raid invites',
  friend_request: 'friend requests',
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export const NotificationBell = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [menuFor, setMenuFor] = useState(null); // index of row whose ⋯ menu is open
  const [mutedTag, setMutedTag] = useState(null); // last-muted tag, for "undo" toast text
  const wrapperRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close dropdown whenever the route changes (cleanliness — prevents the
  // dropdown from briefly lingering after item click → navigation).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  const fetchInbox = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/api/push/inbox?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      // best-effort — silent fail keeps the bell out of the user's way
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial load + 60s poll
  useEffect(() => {
    if (!token) return undefined;
    fetchInbox();
    const id = setInterval(fetchInbox, POLL_MS);
    return () => clearInterval(id);
  }, [token, fetchInbox]);

  // Refresh when service worker delivers a push (best-effort heuristic).
  useEffect(() => {
    if (!navigator?.serviceWorker) return undefined;
    const handler = (event) => {
      if (event?.data?.type === 'push-received') fetchInbox();
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [fetchInbox]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/api/push/inbox/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnread(0);
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
    } catch {
      // silent
    }
  };

  const clearAll = async () => {
    try {
      await axios.post(`${API}/api/push/history/clear`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems([]);
      setUnread(0);
    } catch {
      // silent
    }
  };

  const onClickItem = (item) => {
    setOpen(false);
    markAllRead();
    if (item.url) {
      // Internal SPA routes start with /; otherwise fall back to default nav
      if (item.url.startsWith('http')) {
        window.location.href = item.url;
      } else {
        navigate(item.url);
      }
    }
  };

  const muteTag = async (tag) => {
    if (!tag) return;
    try {
      await axios.put(
        `${API}/api/push/preferences`,
        { preferences: { [tag]: false } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Drop everything of this tag from the inbox so it visually disappears
      setItems((prev) => prev.filter((i) => i.tag !== tag));
      setMutedTag(tag);
      setMenuFor(null);
      // Auto-clear the mute toast after 4s
      setTimeout(() => setMutedTag((cur) => (cur === tag ? null : cur)), 4000);
    } catch {
      // silent
    }
  };

  const unmuteLastTag = async () => {
    if (!mutedTag) return;
    try {
      await axios.put(
        `${API}/api/push/preferences`,
        { preferences: { [mutedTag]: true } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMutedTag(null);
      fetchInbox();
    } catch {
      // silent
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      fetchInbox();
      if (unread > 0) markAllRead();
    }
  };

  if (!token) return null;

  return (
    <div ref={wrapperRef} className="relative" data-testid="notification-bell-wrapper">
      <button
        type="button"
        onClick={toggle}
        className="relative p-1.5 rounded-lg text-babylon-text-dim hover:text-babylon-gold hover:bg-babylon-gold/10 transition-colors"
        title="Notifications"
        aria-label="Open notifications"
        data-testid="notification-bell-btn"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-pink-500 text-white text-[9px] font-bold flex items-center justify-center shadow-[0_0_8px_rgba(236,72,153,0.6)]"
            data-testid="notification-bell-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 max-h-[28rem] rounded-lg border border-babylon-gold/30 bg-babylon-obsidian/95 backdrop-blur-md shadow-2xl shadow-black/50 z-[60] overflow-hidden flex flex-col"
          data-testid="notification-bell-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-babylon-gold/20">
            <div className="flex items-center gap-1.5">
              <Inbox size={14} className="text-babylon-gold" />
              <span className="text-sm font-bold text-babylon-gold">Notifications</span>
            </div>
            <div className="flex items-center gap-1">
              {items.length > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="text-[10px] px-1.5 py-0.5 rounded text-babylon-text-dim hover:text-babylon-gold hover:bg-babylon-gold/10"
                  title="Mark all read"
                  data-testid="notification-mark-read-btn"
                >
                  <Check size={12} className="inline mr-0.5" /> Read
                </button>
              )}
              {items.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-[10px] px-1.5 py-0.5 rounded text-babylon-text-dim hover:text-red-400 hover:bg-red-400/10"
                  title="Clear all"
                  data-testid="notification-clear-btn"
                >
                  <X size={12} className="inline mr-0.5" /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto" data-testid="notification-list">
            {loading && items.length === 0 && (
              <div className="py-8 text-center text-xs text-babylon-text-dim animate-pulse" data-testid="notification-loading">
                Loading…
              </div>
            )}

            {!loading && items.length === 0 && (
              <div className="py-10 text-center text-xs text-babylon-text-dim" data-testid="notification-empty">
                <Bell size={20} className="mx-auto mb-2 opacity-30" />
                <p>No notifications yet.</p>
                <p className="text-[10px] mt-1 opacity-70">
                  We&apos;ll let you know when something cosmic happens.
                </p>
              </div>
            )}

            {items.map((item, idx) => (
              <div
                key={`${item.tag}-${item.sent_at}-${idx}`}
                className={`relative border-b border-babylon-gold/10 last:border-b-0 ${
                  item.read
                    ? 'opacity-80'
                    : 'bg-babylon-gold/5'
                }`}
                data-testid={`notification-item-${idx}`}
              >
                <button
                  type="button"
                  onClick={() => onClickItem(item)}
                  className="w-full text-left px-3 py-2 pr-9 flex items-start gap-2 transition-colors hover:bg-babylon-gold/10"
                  data-testid={`notification-item-link-${idx}`}
                >
                  <span className="text-base leading-none flex-shrink-0 mt-0.5">
                    {ICONS[item.tag] || '🔔'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-bold text-babylon-text truncate">
                        {item.title || 'Notification'}
                      </span>
                      {!item.read && (
                        <span
                          className="w-1.5 h-1.5 rounded-full bg-pink-500 flex-shrink-0 shadow-[0_0_4px_rgba(236,72,153,0.8)]"
                          data-testid={`notification-unread-dot-${idx}`}
                        />
                      )}
                    </div>
                    <p className="text-[10px] text-babylon-text-dim mt-0.5 line-clamp-2 leading-snug">
                      {item.body}
                    </p>
                    <p className="text-[9px] text-babylon-text-dim/60 mt-0.5">
                      {timeAgo(item.sent_at)}
                    </p>
                  </div>
                </button>

                {/* ⋯ menu trigger */}
                {item.tag && TAG_LABEL[item.tag] && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuFor(menuFor === idx ? null : idx);
                    }}
                    className="absolute top-2 right-1.5 p-1 rounded text-babylon-text-dim/70 hover:text-babylon-gold hover:bg-babylon-gold/10"
                    title="More options"
                    data-testid={`notification-item-menu-${idx}`}
                  >
                    <MoreHorizontal size={12} />
                  </button>
                )}

                {menuFor === idx && (
                  <div
                    className="absolute top-7 right-1.5 w-44 rounded-md border border-babylon-gold/30 bg-babylon-obsidian shadow-lg z-[2] overflow-hidden"
                    onMouseLeave={() => setMenuFor(null)}
                    data-testid={`notification-item-menu-popover-${idx}`}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        muteTag(item.tag);
                      }}
                      className="w-full px-2 py-1.5 flex items-center gap-1.5 text-[10px] text-left text-babylon-text hover:bg-red-500/15 hover:text-red-300"
                      data-testid={`notification-mute-${item.tag}-${idx}`}
                    >
                      <BellOff size={11} />
                      Mute {TAG_LABEL[item.tag]}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mute toast — quick undo */}
          {mutedTag && (
            <div
              className="px-3 py-2 border-t border-babylon-gold/20 bg-babylon-void/60 text-[10px] flex items-center justify-between gap-2"
              data-testid="notification-mute-toast"
            >
              <span className="text-babylon-text-dim flex items-center gap-1">
                <BellOff size={10} className="text-red-300" />
                Muted {TAG_LABEL[mutedTag]}
              </span>
              <button
                type="button"
                onClick={unmuteLastTag}
                className="text-pink-300 hover:text-pink-200 font-bold uppercase text-[9px] tracking-wider"
                data-testid="notification-unmute-btn"
              >
                Undo
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
